import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "../context/AuthContext";
import {
  listCompanyUsers,
  listVacationPlans,
  getVacationBalance,
  createVacationPlanApi,
  updateVacationPlanApi,
  submitVacationPlanApi,
  approveVacationPlanApi,
  rejectVacationPlanApi,
  cancelVacationPlanApi,
  deleteVacationPlanApi,
  exportVacationPlans,
  listLocations,
} from "../api";
import { ROLES } from "../constants";
import { todayParts } from "../utils/dates";
import LocationFilter, { applyLocationFilter } from "../components/LocationFilter";
import AppPage from "../components/AppPage";
import "../VacationSchedule.css";

const STATUS_CLASS = {
  draft: "secondary",
  pending: "warning",
  approved: "success",
  rejected: "danger",
  cancelled: "text-bg-light text-muted",
};

const fmtDate = (iso) => {
  if (!iso) return "—";
  const [y, m, d] = iso.split("-");
  return `${d}.${m}.${y}`;
};

const blankForm = {
  user_id: "",
  date_from: "",
  date_to: "",
  employee_comment: "",
};

export default function VacationSchedule() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const tp = todayParts();
  const [year, setYear] = useState(tp.year);
  const [scope, setScope] = useState("mine");
  const [users, setUsers] = useState([]);
  const [plans, setPlans] = useState([]);
  const [pendingCount, setPendingCount] = useState(0);
  const [balances, setBalances] = useState({});
  const [locations, setLocations] = useState([]);
  const [locationFilter, setLocationFilter] = useState("all");
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [createForUserId, setCreateForUserId] = useState(null);
  const [rejectPlan, setRejectPlan] = useState(null);
  const [rejectReason, setRejectReason] = useState("");
  const [saveError, setSaveError] = useState("");
  const [pageError, setPageError] = useState("");
  const [pageSuccess, setPageSuccess] = useState("");
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("all"); // all|unplanned|draft|pending|approved|rejected|cancelled
  const [exporting, setExporting] = useState(false);
  const [viewMode, setViewMode] = useState("list"); // list|lane (lane = HR gantt)

  const isAdmin = user && [ROLES.DIRECTOR, ROLES.HR].includes(user.role);
  const isManager = user?.role === ROLES.MANAGER;
  const canApprove = isAdmin;

  const visibleUsers = useMemo(() => {
    if (!user) return [];
    let base;
    if (scope === "company" && isAdmin) base = users;
    else if (scope === "team" && (isAdmin || isManager)) {
      base = isAdmin
        ? users
        : users.filter(u => u.user_id === user.user_id || u.manager_id === user.user_id);
    } else {
      base = users.filter(u => u.user_id === user.user_id);
    }
    let result = applyLocationFilter(base.filter(u => !u.fired_at), locationFilter);
    // Текущий пользователь всегда виден в «Мой график», даже если его нет в списке company users
    if (scope === "mine" && !result.some(u => u.user_id === user.user_id)) {
      result = [{
        user_id: user.user_id,
        name: user.name || user.email || t("vacation_schedule.scope_mine"),
        vacation_days_limit: null,
        fired_at: null,
      }, ...result];
    }
    return result;
  }, [users, user, scope, isAdmin, isManager, locationFilter, t]);

  const plansByUser = useMemo(() => {
    const m = new Map();
    for (const p of plans) {
      if (!m.has(p.user_id)) m.set(p.user_id, []);
      m.get(p.user_id).push(p);
    }
    for (const [, arr] of m) {
      arr.sort((a, b) => (a.date_from || "").localeCompare(b.date_from || ""));
    }
    return m;
  }, [plans]);

  const filteredUsers = useMemo(() => {
    const qq = (q || "").trim().toLowerCase();
    if (!qq) return visibleUsers;
    return visibleUsers.filter(u => (u.name || "").toLowerCase().includes(qq));
  }, [visibleUsers, q]);

  const usersWithPlansInYear = useMemo(() => {
    const set = new Set();
    for (const p of plans) {
      if (p.planning_year === year) set.add(p.user_id);
    }
    return set;
  }, [plans, year]);

  const usersMatchingStatus = useMemo(() => {
    if (statusFilter === "all") return filteredUsers;
    if (statusFilter === "unplanned") {
      return filteredUsers.filter(u => !usersWithPlansInYear.has(u.user_id));
    }
    return filteredUsers.filter(u => {
      const list = plansByUser.get(u.user_id) || [];
      return list.some(p => p.planning_year === year && p.status === statusFilter);
    });
  }, [filteredUsers, plansByUser, statusFilter, usersWithPlansInYear, year]);

  const missingUsers = useMemo(() => {
    return filteredUsers.filter(u => !usersWithPlansInYear.has(u.user_id));
  }, [filteredUsers, usersWithPlansInYear]);

  const reload = async () => {
    setLoading(true);
    try {
      const [u, data, locs] = await Promise.all([
        listCompanyUsers(),
        listVacationPlans({ year, scope }),
        listLocations().catch(() => []),
      ]);
      setUsers(u || []);
      setPlans(data?.plans || []);
      setPendingCount(data?.pending_count || 0);
      setLocations(locs || []);

      const balMap = {};
      const targets = scope === "mine"
        ? [user.user_id]
        : visibleUsers.map(x => x.user_id);
      await Promise.all(targets.map(async (uid) => {
        try {
          balMap[uid] = await getVacationBalance(year, uid);
        } catch { /* ignore */ }
      }));
      setBalances(balMap);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!user) return;
    if (scope === "company" && !isAdmin) setScope("mine");
    else if (scope === "team" && !isAdmin && !isManager) setScope("mine");
  }, [user, scope, isAdmin, isManager]);

  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [year, scope]);

  const openCreate = (targetUserId) => {
    setEditing(null);
    setCreateForUserId(targetUserId || user.user_id);
    setSaveError("");
    setShowModal(true);
  };

  const onSave = async (form) => {
    setSaveError("");
    try {
      if (editing) {
        await updateVacationPlanApi(editing.plan_id, {
          date_from: form.date_from,
          date_to: form.date_to,
          employee_comment: form.employee_comment,
        });
      } else {
        await createVacationPlanApi({
          user_id: form.user_id,
          planning_year: year,
          date_from: form.date_from,
          date_to: form.date_to,
          employee_comment: form.employee_comment,
        });
      }
      setShowModal(false);
      setEditing(null);
      await reload();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const msg = typeof detail === "string" ? detail
        : Array.isArray(detail) ? detail.map(x => x.msg || x).join("; ")
        : (e.message || t("vacation_schedule.save_error"));
      setSaveError(msg);
    }
  };

  const act = async (fn, successMsg) => {
    try {
      await fn();
      setPageError("");
      if (successMsg) {
        setPageSuccess(successMsg);
        setTimeout(() => setPageSuccess(""), 4000);
      }
      await reload();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const msg = typeof detail === "string" ? detail : (e.message || t("common.error"));
      setPageError(msg);
      setPageSuccess("");
    }
  };

  const allowedTargetUsers = useMemo(() => {
    if (isAdmin) return users.filter(u => !u.fired_at);
    if (isManager) return users.filter(u => !u.fired_at && (u.user_id === user.user_id || u.manager_id === user.user_id));
    return users.filter(u => u.user_id === user.user_id);
  }, [users, user, isAdmin, isManager]);

  const yearMeta = useMemo(() => buildYearMeta(year), [year]);

  return (
    <AppPage testId="vacation-schedule-page" bodyScroll="scroll">
      <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
        <h4 className="fw-bold mb-0">
          <i className="bi bi-sun text-info me-2"></i>
          {t("vacation_schedule.title")}
        </h4>
        <div className="d-flex gap-2 flex-wrap align-items-center">
          <input className="form-control" style={{ width: 220 }}
                 value={q} onChange={(e) => setQ(e.target.value)}
                 placeholder={t("vacation_schedule.search_placeholder")}
                 data-testid="vs-search" />
          <select className="form-select" style={{ width: 180 }} value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  data-testid="vs-status-filter">
            <option value="all">{t("vacation_schedule.filter_all")}</option>
            <option value="unplanned">{t("vacation_schedule.status_unplanned")}</option>
            <option value="draft">{t("vacation_schedule.status_draft")}</option>
            <option value="pending">{t("vacation_schedule.status_pending")}</option>
            <option value="approved">{t("vacation_schedule.status_approved")}</option>
            <option value="rejected">{t("vacation_schedule.status_rejected")}</option>
            <option value="cancelled">{t("vacation_schedule.status_cancelled")}</option>
          </select>
          {isAdmin && (
            <LocationFilter locations={locations} users={users}
                            value={locationFilter} onChange={setLocationFilter}
                            testid="vs-location-filter" />
          )}
          <select className="form-select" style={{ width: 110 }} value={year}
                  onChange={(e) => setYear(Number(e.target.value))}
                  data-testid="vs-year-select">
            {[year - 1, year, year + 1].map(y => <option key={y} value={y}>{y}</option>)}
          </select>
          {isAdmin && (
            <div className="btn-group" role="group" aria-label={t("vacation_schedule.view_mode")}>
              <button className={`btn btn-outline-secondary btn-sm ${viewMode === "list" ? "active" : ""}`}
                      onClick={() => setViewMode("list")} data-testid="vs-view-list">
                <i className="bi bi-list-ul me-1"></i>{t("vacation_schedule.view_list")}
              </button>
              <button className={`btn btn-outline-secondary btn-sm ${viewMode === "lane" ? "active" : ""}`}
                      onClick={() => setViewMode("lane")} data-testid="vs-view-lane">
                <i className="bi bi-bar-chart-steps me-1"></i>{t("vacation_schedule.view_lane")}
              </button>
            </div>
          )}
          {isAdmin && (
            <>
              <button className="btn btn-outline-success btn-sm" disabled={exporting}
                      onClick={async () => {
                        setExporting(true);
                        try { await exportVacationPlans(year, "xlsx"); }
                        catch (e) {
                          const detail = e?.response?.data?.detail;
                          const msg = typeof detail === "string" ? detail : (e.message || t("common.error"));
                          setPageError(msg);
                        }
                        finally { setExporting(false); }
                      }}
                      data-testid="vs-export-xlsx"
                      title={t("vacation_schedule.export_xlsx_title")}>
                <i className="bi bi-file-earmark-excel me-1"></i>
                {t("vacation_schedule.export_xlsx")}
              </button>
              <button className="btn btn-outline-danger btn-sm" disabled={exporting}
                      onClick={async () => {
                        setExporting(true);
                        try { await exportVacationPlans(year, "pdf"); }
                        catch (e) {
                          const detail = e?.response?.data?.detail;
                          const msg = typeof detail === "string" ? detail : (e.message || t("common.error"));
                          setPageError(msg);
                        }
                        finally { setExporting(false); }
                      }}
                      data-testid="vs-export-pdf"
                      title={t("vacation_schedule.export_pdf_title")}>
                <i className="bi bi-file-earmark-pdf me-1"></i>
                {t("vacation_schedule.export_pdf")}
              </button>
            </>
          )}
          <button className="btn btn-primary btn-sm" onClick={() => openCreate()}
                  data-testid="vs-add-btn">
            <i className="bi bi-plus-lg me-1"></i>{t("vacation_schedule.add")}
          </button>
        </div>
      </div>

      {(isAdmin || isManager) && (
        <div className="d-flex flex-wrap gap-2 mb-3 small">
          <span className="badge text-bg-light">
            {t("vacation_schedule.kpi_employees", { n: filteredUsers.length })}
          </span>
          <span className={`badge ${missingUsers.length > 0 ? "text-bg-warning" : "text-bg-light"}`}>
            {t("vacation_schedule.kpi_missing", { n: missingUsers.length })}
          </span>
          {isAdmin && (
            <span className={`badge ${pendingCount > 0 ? "text-bg-danger" : "text-bg-light"}`}>
              {t("vacation_schedule.kpi_pending", { n: pendingCount })}
            </span>
          )}
        </div>
      )}

      <ul className="nav nav-pills mb-3 flex-wrap gap-1">
        <li className="nav-item">
          <button className={`nav-link ${scope === "mine" ? "active" : ""}`}
                  onClick={() => setScope("mine")} data-testid="vs-scope-mine">
            {t("vacation_schedule.scope_mine")}
          </button>
        </li>
        {(isManager || isAdmin) && (
          <li className="nav-item">
            <button className={`nav-link ${scope === "team" ? "active" : ""}`}
                    onClick={() => setScope("team")} data-testid="vs-scope-team">
              {t("vacation_schedule.scope_team")}
            </button>
          </li>
        )}
        {isAdmin && (
          <li className="nav-item">
            <button className={`nav-link ${scope === "company" ? "active" : ""}`}
                    onClick={() => setScope("company")} data-testid="vs-scope-company">
              {t("vacation_schedule.scope_company")}
              {pendingCount > 0 && <span className="badge bg-danger ms-2">{pendingCount}</span>}
            </button>
          </li>
        )}
      </ul>

      {pageError && (
        <div className="alert alert-danger py-2 small" role="alert">{pageError}</div>
      )}
      {pageSuccess && (
        <div className="alert alert-success py-2 small" role="alert">{pageSuccess}</div>
      )}

      {loading && <div className="text-muted py-3">{t("common.loading")}</div>}

      {!loading && isAdmin && viewMode === "lane" && (
        <VacationLaneView
          t={t}
          year={year}
          yearMeta={yearMeta}
          users={usersMatchingStatus}
          plans={plans}
          statusFilter={statusFilter}
        />
      )}

      {!loading && (!isAdmin || viewMode === "list") && usersMatchingStatus.map(u => {
        let userPlans = plansByUser.get(u.user_id) || [];
        if (statusFilter !== "all" && statusFilter !== "unplanned") {
          userPlans = userPlans.filter(p => p.planning_year === year && p.status === statusFilter);
        }
        const bal = balances[u.user_id];
        const limit = bal?.limit ?? u.vacation_days_limit ?? 24;
        const remaining = bal?.remaining ?? limit;
        return (
          <div key={u.user_id} className="card card-soft mb-3" data-testid={`vs-user-${u.user_id}`}>
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-start flex-wrap gap-2 mb-2">
                <div>
                  <h6 className="fw-semibold mb-1">{u.name}</h6>
                  <div className="small text-muted">
                    {t("vacation_schedule.balance_line", {
                      limit,
                      approved: bal?.approved_days ?? 0,
                      pending: bal?.pending_days ?? 0,
                      remaining,
                    })}
                  </div>
                </div>
                {(isAdmin || isManager || u.user_id === user.user_id) && (
                  <button className="btn btn-outline-primary btn-sm"
                          onClick={() => openCreate(u.user_id)}
                          data-testid={`vs-add-${u.user_id}`}>
                    <i className="bi bi-plus me-1"></i>{t("vacation_schedule.add_period")}
                  </button>
                )}
              </div>

              {userPlans.length === 0 ? (
                <div className="small">
                  {statusFilter === "unplanned" ? (
                    <span className="badge bg-secondary">{t("vacation_schedule.status_unplanned")}</span>
                  ) : (
                    <span className="text-muted">
                      {statusFilter === "all"
                        ? t("vacation_schedule.empty_user")
                        : t("vacation_schedule.empty_user_filtered")}
                    </span>
                  )}
                </div>
              ) : (
                <div className="table-responsive">
                  <table className="table table-sm align-middle mb-0">
                    <thead>
                      <tr className="small text-muted">
                        <th>{t("vacation_schedule.col_period")}</th>
                        <th>{t("vacation_schedule.col_days")}</th>
                        <th>{t("common.status")}</th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      {userPlans.map(p => (
                        <tr key={p.plan_id} data-testid={`vs-plan-${p.plan_id}`}>
                          <td>
                            {fmtDate(p.date_from)} — {fmtDate(p.date_to)}
                            {p.employee_comment && (
                              <div className="small text-muted">{p.employee_comment}</div>
                            )}
                            {p.rejection_reason && (
                              <div className="small text-danger">{p.rejection_reason}</div>
                            )}
                          </td>
                          <td className="text-nowrap small">
                            {t("vacation_schedule.days_line", {
                              cal: p.calendar_days,
                              work: p.work_days,
                            })}
                          </td>
                          <td>
                            <span className={`badge bg-${STATUS_CLASS[p.status] || "secondary"}`}>
                              {t(`vacation_schedule.status_${p.status}`)}
                            </span>
                          </td>
                          <td className="text-end text-nowrap">
                            {p.status === "draft" && (
                              <>
                                <button className="btn btn-link btn-sm p-0 me-2"
                                        onClick={() => { setEditing(p); setShowModal(true); }}
                                        data-testid={`vs-edit-${p.plan_id}`}>
                                  {t("common.edit")}
                                </button>
                                <button className="btn btn-link btn-sm p-0 me-2"
                                        onClick={() => act(() => submitVacationPlanApi(p.plan_id), t("vacation_schedule.toast_submitted"))}
                                        data-testid={`vs-submit-${p.plan_id}`}>
                                  {t("vacation_schedule.submit")}
                                </button>
                                <button className="btn btn-link btn-sm text-danger p-0"
                                        onClick={() => act(() => deleteVacationPlanApi(p.plan_id))}
                                        data-testid={`vs-delete-${p.plan_id}`}>
                                  {t("common.delete")}
                                </button>
                              </>
                            )}
                            {p.status === "pending" && canApprove && (
                              <>
                                <button className="btn btn-success btn-sm me-1"
                                        onClick={() => act(() => approveVacationPlanApi(p.plan_id), t("vacation_schedule.toast_approved"))}
                                        data-testid={`vs-approve-${p.plan_id}`}>
                                  {t("vacation_schedule.approve")}
                                </button>
                                <button className="btn btn-outline-danger btn-sm me-1"
                                        onClick={() => { setRejectPlan(p); setRejectReason(""); }}
                                        data-testid={`vs-reject-${p.plan_id}`}>
                                  {t("vacation_schedule.reject")}
                                </button>
                              </>
                            )}
                            {p.status === "pending" && !canApprove && (
                              <button className="btn btn-link btn-sm text-danger p-0"
                                      onClick={() => act(() => cancelVacationPlanApi(p.plan_id))}
                                      data-testid={`vs-cancel-${p.plan_id}`}>
                                {t("vacation_schedule.cancel")}
                              </button>
                            )}
                            {p.status === "rejected" && (
                              <button className="btn btn-link btn-sm p-0"
                                      onClick={() => { setEditing(p); setShowModal(true); }}
                                      data-testid={`vs-reedit-${p.plan_id}`}>
                                {t("vacation_schedule.revise")}
                              </button>
                            )}
                            {p.status === "approved" && canApprove && (
                              <button className="btn btn-link btn-sm text-danger p-0"
                                      onClick={() => act(() => cancelVacationPlanApi(p.plan_id))}
                                      data-testid={`vs-cancel-approved-${p.plan_id}`}>
                                {t("vacation_schedule.cancel")}
                              </button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        );
      })}

      {!loading && usersMatchingStatus.length === 0 && (
        <div className="text-center text-muted py-5">
          {statusFilter === "all"
            ? t("vacation_schedule.empty")
            : statusFilter === "unplanned"
              ? t("vacation_schedule.empty_unplanned")
              : t("vacation_schedule.empty_user_filtered")}
        </div>
      )}

      {showModal && (
        <VacationPlanModal
          key={editing?.plan_id || `new-${createForUserId || user.user_id}`}
          year={year}
          initial={editing}
          defaultUserId={createForUserId || user.user_id}
          targetUserName={
            (users.find(u => u.user_id === (createForUserId || user.user_id))?.name)
            || user.name
            || ""
          }
          users={allowedTargetUsers}
          currentUserId={user.user_id}
          isAdmin={isAdmin}
          isManager={isManager}
          onClose={() => { setShowModal(false); setEditing(null); setCreateForUserId(null); setSaveError(""); }}
          onSave={onSave}
          saveError={saveError}
        />
      )}

      {rejectPlan && (
        <div className="modal d-block" style={{ background: "rgba(0,0,0,0.5)" }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">{t("vacation_schedule.reject_title")}</h5>
                <button type="button" className="btn-close" onClick={() => setRejectPlan(null)} />
              </div>
              <div className="modal-body">
                <label className="form-label small">{t("vacation_schedule.reject_reason")}</label>
                <textarea className="form-control" rows={3} value={rejectReason}
                          onChange={(e) => setRejectReason(e.target.value)}
                          data-testid="vs-reject-reason" />
              </div>
              <div className="modal-footer">
                <button className="btn btn-outline-secondary" onClick={() => setRejectPlan(null)}>
                  {t("common.cancel")}
                </button>
                <button className="btn btn-danger" disabled={!rejectReason.trim()}
                        onClick={async () => {
                          await act(() => rejectVacationPlanApi(rejectPlan.plan_id, { reason: rejectReason }),
                            t("vacation_schedule.toast_rejected"));
                          setRejectPlan(null);
                        }}
                        data-testid="vs-reject-confirm">
                  {t("vacation_schedule.reject")}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </AppPage>
  );
}

function VacationLaneView({ t, year, yearMeta, users, plans, statusFilter }) {
  const byUser = useMemo(() => {
    const m = new Map();
    for (const p of plans || []) {
      if (p.planning_year !== year) continue;
      if (statusFilter !== "all" && statusFilter !== "unplanned" && p.status !== statusFilter) continue;
      if (!m.has(p.user_id)) m.set(p.user_id, []);
      m.get(p.user_id).push(p);
    }
    for (const [, arr] of m) arr.sort((a, b) => (a.date_from || "").localeCompare(b.date_from || ""));
    return m;
  }, [plans, year, statusFilter]);

  return (
    <div className="card card-soft" data-testid="vs-lane">
      <div className="card-body">
        <div className="vs-lane-wrap">
          <div className="vs-lane-header">
            <div className="vs-lane-name-col">{t("employees.table.name")}</div>
            <div className="vs-lane-grid">
              {yearMeta.months.map(m => (
                <div key={m.key} className="vs-lane-month" style={{ width: m.days * 10 }}>
                  {t(`months.${m.label}`)}
                </div>
              ))}
            </div>
          </div>

          <div className="vs-lane-body">
            {users.map(u => {
              const list = byUser.get(u.user_id) || [];
              return (
                <div key={u.user_id} className="vs-lane-row" data-testid={`vs-lane-${u.user_id}`}>
                  <div className="vs-lane-name-col">
                    <div className="text-truncate" title={u.name}>{u.name}</div>
                  </div>
                  <div className="vs-lane-grid">
                    <div className="vs-lane-track" style={{ width: yearMeta.totalDays * 10 }}>
                      {list.map(p => {
                        const left = yearMeta.dayIndex(p.date_from) * 10;
                        const width = Math.max(1, (yearMeta.daySpan(p.date_from, p.date_to)) * 10);
                        const cls = `vs-bar vs-bar-${p.status}`;
                        return (
                          <div key={p.plan_id} className={cls} style={{ left, width }}
                               title={`${u.name}: ${fmtDate(p.date_from)}—${fmtDate(p.date_to)} (${p.calendar_days} дн) · ${t(`vacation_schedule.status_${p.status}`)}`} />
                        );
                      })}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
        <div className="small text-muted mt-2">
          {t("vacation_schedule.lane_hint")}
        </div>
      </div>
    </div>
  );
}

function buildYearMeta(year) {
  const months = [
    { key: "jan", m: 1 }, { key: "feb", m: 2 }, { key: "mar", m: 3 }, { key: "apr", m: 4 },
    { key: "may", m: 5 }, { key: "jun", m: 6 }, { key: "jul", m: 7 }, { key: "aug", m: 8 },
    { key: "sep", m: 9 }, { key: "oct", m: 10 }, { key: "nov", m: 11 }, { key: "dec", m: 12 },
  ];
  const daysInMonth = (y, m1) => new Date(y, m1, 0).getDate();
  const monthMeta = months.map(x => ({
    key: x.key,
    label: x.key, // translated in UI with months.*
    days: daysInMonth(year, x.m),
  }));
  const totalDays = monthMeta.reduce((s, x) => s + x.days, 0);
  const monthStarts = [];
  let acc = 0;
  for (const mm of monthMeta) { monthStarts.push(acc); acc += mm.days; }

  const parseISO = (s) => {
    if (!s || typeof s !== "string") return null;
    const [yy, mo, dd] = s.split("-").map(Number);
    if (!yy || !mo || !dd) return null;
    return new Date(yy, mo - 1, dd);
  };

  const dayIndex = (iso) => {
    const d = parseISO(iso);
    if (!d || d.getFullYear() !== year) return 0;
    const start = new Date(year, 0, 1);
    return Math.max(0, Math.min(totalDays - 1, Math.floor((d - start) / 86400000)));
  };
  const daySpan = (fromIso, toIso) => {
    const a = parseISO(fromIso);
    const b = parseISO(toIso);
    if (!a || !b) return 1;
    const start = new Date(year, 0, 1);
    const ia = Math.floor((a - start) / 86400000);
    const ib = Math.floor((b - start) / 86400000);
    return Math.max(1, Math.min(totalDays, (ib - ia + 1)));
  };

  return {
    months: monthMeta.map((m, i) => ({ ...m, label: m.key, start: monthStarts[i] })),
    totalDays,
    dayIndex,
    daySpan,
  };
}

function VacationPlanModal({
  year, initial, defaultUserId, targetUserName, users, currentUserId,
  isAdmin, isManager, onClose, onSave, saveError = "",
}) {
  const { t } = useTranslation();
  const resolvedUserId = initial?.user_id || defaultUserId || currentUserId;
  const planningForSelf = !initial && resolvedUserId === currentUserId;
  const explicitTarget = Boolean(!initial && defaultUserId && defaultUserId !== currentUserId);
  const showTargetLabel = !initial && (planningForSelf || explicitTarget);

  const [form, setForm] = useState(() => initial ? {
    user_id: initial.user_id,
    date_from: initial.date_from,
    date_to: initial.date_to,
    employee_comment: initial.employee_comment || "",
  } : {
    user_id: resolvedUserId,
    date_from: "",
    date_to: "",
    employee_comment: "",
  });

  // Выбор сотрудника — только HR/начальник, и только если сотрудник ещё не выбран (кнопка «+» в шапке на вкладке Команда)
  const showEmployeePicker = (isAdmin || isManager) && !initial && !planningForSelf && !explicitTarget;

  const canSave = form.date_from && form.date_to && form.user_id
    && form.date_from <= form.date_to
    && form.date_from.startsWith(String(year))
    && form.date_to.startsWith(String(year));

  return (
    <div className="modal d-block" style={{ background: "rgba(0,0,0,0.5)" }}>
      <div className="modal-dialog">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">
              {initial ? t("vacation_schedule.edit") : t("vacation_schedule.add")}
            </h5>
            <button type="button" className="btn-close" onClick={onClose} />
          </div>
          <div className="modal-body">
            {saveError && (
              <div className="alert alert-danger py-2 small mb-3" role="alert" data-testid="vs-form-error">
                {saveError}
              </div>
            )}
            <div className="row g-3">
              {showTargetLabel && (
                <div className="col-12">
                  <div className="small text-muted">{t("vacation_schedule.planning_for")}</div>
                  <div className="fw-semibold">{targetUserName}</div>
                </div>
              )}
              {showEmployeePicker && (
                <div className="col-12">
                  <label className="form-label small">{t("vacation_schedule.f_employee")}</label>
                  <select className="form-select" value={form.user_id}
                          onChange={(e) => setForm({ ...form, user_id: e.target.value })}
                          data-testid="vs-form-employee">
                    {users.length === 0 && (
                      <option value="">{t("common.loading")}</option>
                    )}
                    {users.map(u => (
                      <option key={u.user_id} value={u.user_id}>{u.name}</option>
                    ))}
                  </select>
                </div>
              )}
              <div className="col-md-6">
                <label className="form-label small">{t("vacation_schedule.f_start")}</label>
                <input type="date" className="form-control" value={form.date_from}
                       min={`${year}-01-01`} max={`${year}-12-31`}
                       onChange={(e) => setForm({ ...form, date_from: e.target.value })}
                       data-testid="vs-form-from" />
              </div>
              <div className="col-md-6">
                <label className="form-label small">{t("vacation_schedule.f_end")}</label>
                <input type="date" className="form-control" value={form.date_to}
                       min={`${year}-01-01`} max={`${year}-12-31`}
                       onChange={(e) => setForm({ ...form, date_to: e.target.value })}
                       data-testid="vs-form-to" />
              </div>
              <div className="col-12">
                <label className="form-label small">{t("vacation_schedule.f_comment")}</label>
                <textarea className="form-control" rows={2} value={form.employee_comment}
                          onChange={(e) => setForm({ ...form, employee_comment: e.target.value })}
                          data-testid="vs-form-comment" />
              </div>
              <div className="col-12 small text-muted">
                {t("vacation_schedule.hint_calendar_days", { year })}
              </div>
            </div>
          </div>
          <div className="modal-footer">
            <button className="btn btn-outline-secondary" onClick={onClose}>{t("common.cancel")}</button>
            <button className="btn btn-primary" disabled={!canSave}
                    onClick={() => onSave(form)} data-testid="vs-form-save">
              {t("common.save")}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
